"use client";

import { c as _c } from "react/compiler-runtime";
import { type HierarchiesControllerGetChildrenQueryParamsScopeTypeEnumKey, hierarchiesControllerGetChildrenQueryParamsScopeTypeEnum } from "@dc/gateway-jsonapi";
import { parseAsString, parseAsStringEnum, useQueryStates } from "@dc/ui";
import { useDebouncedCallback } from "@dc/ui/hooks/useDebouncedCallback";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { DEFAULT_HIERARCHY_SCOPE_TYPE, type HierarchyScopeParams } from "@/data/hierarchy/constants";
import { useTeamsContext } from "@/hooks/useTeamsContext";
import { isProcorePath } from "@/lib/isProcorePath";
import { isProjectNonFolderTabPath } from "@/lib/isProjectNonFolderTabPath";
import { isProjectsListPath } from "@/lib/isProjectsListPath";
import { stripOrgTeamPrefix } from "@/lib/orgTeamPath.shared";
const VALID_SCOPE_TYPES = Object.values(hierarchiesControllerGetChildrenQueryParamsScopeTypeEnum) as HierarchiesControllerGetChildrenQueryParamsScopeTypeEnumKey[];
export type ActiveHierarchyScope = HierarchyScopeParams;
type SetActiveScopeOptions = {
  history?: "push" | "replace";
};
interface UseActiveHierarchyScopeOptions {
  /**
   * When true, the hook does not auto-seed `scopeId`/`scopeType` query params
   * into the URL. Use on routes that don't read scope from the URL (e.g.
   * project Playbooks) to avoid polluting the URL with irrelevant params.
   */
  skipScopeInjection?: boolean;
}
export function useActiveHierarchyScope(t0) {
  const $ = _c(36);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    skipScopeInjection: skipFromCaller
  } = t1;
  const {
    currentScopeId
  } = useTeamsContext();
  const pathname = usePathname();
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = /^\/profile(?:\/|$)/;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== pathname) {
    t3 = t2.test(stripOrgTeamPrefix(pathname));
    $[3] = pathname;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const isProfileRoute = t3;
  let t4;
  if ($[5] !== isProfileRoute || $[6] !== pathname || $[7] !== skipFromCaller) {
    t4 = skipFromCaller || isProfileRoute || pathname.includes("/settings/admin/") || pathname.includes("/settings/project-settings/") || isProcorePath(pathname) || isProjectsListPath(pathname) || isProjectNonFolderTabPath(pathname);
    $[5] = isProfileRoute;
    $[6] = pathname;
    $[7] = skipFromCaller;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const shouldSkipScopeInjection = t4;
  let t5;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      scopeId: parseAsString,
      scopeType: parseAsStringEnum(VALID_SCOPE_TYPES)
    };
    t6 = {
      history: "replace",
      shallow: true,
      scroll: false
    };
    $[9] = t5;
    $[10] = t6;
  } else {
    t5 = $[9];
    t6 = $[10];
  }
  const [query, setQuery] = useQueryStates(t5, t6);
  let t7;
  bb0: {
    if (!currentScopeId) {
      t7 = null;
      break bb0;
    }
    let t8;
    if ($[11] !== currentScopeId) {
      t8 = {
        scopeId: currentScopeId,
        scopeType: DEFAULT_HIERARCHY_SCOPE_TYPE
      };
      $[11] = currentScopeId;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    t7 = t8 satisfies HierarchyScopeParams;
  }
  const fallbackScope = t7;
  const [pendingScope, setPendingScope] = useState(null);
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setPendingScope(null);
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const clearPendingScope = useDebouncedCallback(t8, 500);
  const t9 = query.scopeId || fallbackScope?.scopeId || "";
  const t10 = query.scopeType || fallbackScope?.scopeType || DEFAULT_HIERARCHY_SCOPE_TYPE;
  let t11;
  if ($[14] !== t10 || $[15] !== t9) {
    t11 = {
      scopeId: t9,
      scopeType: t10
    };
    $[14] = t10;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const queryScope = t11;
  const scope = pendingScope ?? queryScope;
  let t12;
  if ($[17] !== fallbackScope?.scopeId || $[18] !== fallbackScope?.scopeType || $[19] !== query.scopeId || $[20] !== query.scopeType || $[21] !== setQuery || $[22] !== shouldSkipScopeInjection) {
    t12 = () => {
      if (shouldSkipScopeInjection) {
        return;
      }
      if (query.scopeId && query.scopeType) {
        return;
      }
      const nextScopeId = query.scopeId || fallbackScope?.scopeId;
      const nextScopeType = query.scopeType || fallbackScope?.scopeType || DEFAULT_HIERARCHY_SCOPE_TYPE;
      if (!nextScopeId) {
        return;
      }
      setQuery({
        scopeId: nextScopeId,
        scopeType: nextScopeType
      }, {
        history: "replace",
        shallow: true,
        scroll: false
      });
    };
    $[17] = fallbackScope?.scopeId;
    $[18] = fallbackScope?.scopeType;
    $[19] = query.scopeId;
    $[20] = query.scopeType;
    $[21] = setQuery;
    $[22] = shouldSkipScopeInjection;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== fallbackScope || $[25] !== query.scopeId || $[26] !== query.scopeType || $[27] !== setQuery || $[28] !== shouldSkipScopeInjection) {
    t13 = [shouldSkipScopeInjection, fallbackScope, query.scopeId, query.scopeType, setQuery];
    $[24] = fallbackScope;
    $[25] = query.scopeId;
    $[26] = query.scopeType;
    $[27] = setQuery;
    $[28] = shouldSkipScopeInjection;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  useEffect(t12, t13);
  let t14;
  if ($[30] !== clearPendingScope || $[31] !== setQuery) {
    t14 = (nextScope, options) => {
      setPendingScope(nextScope);
      clearPendingScope();
      return setQuery(nextScope, {
        history: options?.history ?? "push",
        shallow: true,
        scroll: false
      });
    };
    $[30] = clearPendingScope;
    $[31] = setQuery;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const setScope = t14;
  let t15;
  if ($[33] !== scope || $[34] !== setScope) {
    t15 = {
      scope,
      setScope
    };
    $[33] = scope;
    $[34] = setScope;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  return t15;
}